import React, { useState, useEffect, useCallback } from 'react';
import { getSupabase } from '../lib/supabase.js';
import { ensureAccount } from './LogMovementModal.jsx';
import { findCollision, movementStamp } from '../lib/collision.js';

// In-app mirror of the Telegram Confirm/Edit/Deny flow (task #18). Surfaces
// the detector's PENDING money_movements so drafts can be reviewed in the app,
// not only in Telegram. Confirm → status confirmed (fires the value event);
// Deny → denied (excluded from the clock); Edit → correct the amount.
//
// RLS scopes money_movements to the viewer's household already, so a plain
// select returns only their own pending drafts. After any action we dispatch
// finops:enrollment-changed so the Home data (balances / next step) reloads.
//
// Kraken deposit evidence (T-009, D22 §2 addendum): for ADMIN viewers, rows
// destined to a kareem_kraken account get a one-line evidence note from the
// read-only kraken-read proxy (DepositStatus) — "landed / in flight / no
// match" — so confirming a pool contribution is one click against an
// API-verified arrival instead of a manual check in another tab. Strictly
// evidence, never the trigger: nothing here auto-confirms, per the owner
// decision 2026-08-04. Trust Wallet contributions have no API and show
// nothing. Investors never fetch (the proxy is operator-gated anyway).
//
// TK-C DRAWS (T-009 Build 1, owner decisions 2026-08-08): the scheduled
// taekus-draw-fetch writes each unseen pool-card transaction as a pending
// movement (source='taekus_draw'), and those rows render here with the
// decision-2 fields — date, amount, raw merchant descriptor, card last-4,
// editable suggested loop/step, and the pool-balance effect. Draw rows swap
// the amount editor for a step picker: the Taekus amount is evidence and is
// not editable in-app ("Not mine" + hand-log is the correction path). The
// household of the card, or an admin, confirms (decision 3 — the movement
// RLS already scopes exactly that). A "checked N min ago · Check now" strip
// appears for households with a mapped TK-C card; the button pokes the
// server-side fetcher (taekus-read stays operator-only) behind a 60s
// cooldown. The Taekus API is never the trigger — only the human confirm
// books the draw (D22).

const fmt$ = (n) => (n == null ? '—' : '$' + Math.round(Math.abs(n)).toLocaleString('en-US'));
const fmtCents = (n) => (n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// Exact to the minute — the owner's requirement is that the investor can see
// precisely which entry we mean, not a fuzzy "earlier today".
const fmtStamp = (d) => d.toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});

const S = {
  card: { background: 'var(--surface,#fff)', border: '1px solid #fde68a', borderRadius: 16, padding: '14px 16px', margin: '0 0 16px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
  head: { fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 },
  row: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: '1px solid var(--border,#eef2f7)', flexWrap: 'wrap' },
  amt: { fontSize: 16, fontWeight: 700, color: 'var(--text-1,#0f172a)' },
  flow: { fontSize: 12, color: 'var(--text-3,#64748b)' },
  actions: { display: 'flex', gap: 6, marginLeft: 'auto' },
  btn: { fontSize: 12.5, fontWeight: 600, padding: '7px 11px', borderRadius: 9, border: '1px solid var(--border,#e2e8f0)', background: 'var(--surface,#fff)', color: 'var(--text-2,#334155)', cursor: 'pointer' },
  ok: { background: '#059669', borderColor: '#059669', color: '#fff' },
  no: { background: '#fff', borderColor: '#fecaca', color: '#b91c1c' },
  edit: { display: 'flex', gap: 6, marginLeft: 'auto', alignItems: 'center' },
  input: { width: 90, padding: '6px 8px', borderRadius: 8, border: '1px solid var(--border,#cbd5e1)', fontSize: 13 },
  evOk: { fontSize: 11.5, fontWeight: 700, color: '#059669', marginTop: 2 },
  evWarn: { fontSize: 11.5, fontWeight: 700, color: '#b45309', marginTop: 2 },
  evGray: { fontSize: 11.5, fontWeight: 500, color: 'var(--text-3,#94a3b8)', marginTop: 2 },
  waitPill: { fontSize: 11.5, fontWeight: 700, color: '#92400e', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 999, padding: '6px 11px', whiteSpace: 'nowrap' },
  drawTag: { fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '2px 6px', marginRight: 6 },
  suggest: { fontSize: 12, color: 'var(--text-2,#475569)', marginTop: 2 },
  poolFx: { fontSize: 11.5, fontWeight: 600, color: 'var(--text-2,#475569)', marginTop: 2 },
  degraded: { fontSize: 11.5, fontWeight: 700, color: '#b45309', marginTop: 2 },
  collision: {
    fontSize: 12, fontWeight: 600, color: '#7f1d1d', marginTop: 6,
    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '7px 9px', lineHeight: 1.45,
  },
  strip: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: 'var(--text-3,#64748b)', flexWrap: 'wrap' },
  stripBtn: { fontSize: 12, fontWeight: 700, padding: '5px 10px', borderRadius: 8, border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e', cursor: 'pointer' },
  select: { padding: '6px 8px', borderRadius: 8, border: '1px solid var(--border,#cbd5e1)', fontSize: 12.5, maxWidth: 320 },
  // T-044 attribution line + logged-by pills (the app-wide ActorPill palette:
  // amber = entered by admin, quiet grey = entered by the investor).
  who: { fontSize: 12, fontWeight: 600, color: 'var(--text-2,#475569)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  whoPillAdmin: { fontSize: 9.5, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '2px 6px', borderRadius: 999, whiteSpace: 'nowrap', background: 'var(--amber-bg,#fef3c7)', color: 'var(--amber,#b45309)', border: '1px solid var(--amber-border,#fcd34d)' },
  whoPillSelf: { fontSize: 9.5, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '2px 6px', borderRadius: 999, whiteSpace: 'nowrap', background: 'var(--surface-2,#f1f5f9)', color: 'var(--text-3,#64748b)', border: '1px solid var(--border,#e2e8f0)' },
};

// T-044: name the people behind a pending movement — the account owner who
// sent it (source first; funded_by and dest owner as fallbacks), their
// household when the owner is a sub-account (Zena ≠ Kholy), and who logged
// the row. Renders nothing when the viewer's RLS can't name the owner.
function WhoLine({ m, meta }) {
  const ownerId = (meta.acctOwner || {})[m.source_account_id]
    || m.funded_by_member_id
    || (meta.acctOwner || {})[m.dest_account_id];
  const name = ownerId ? (meta.memberName || {})[ownerId] : null;
  if (!name) return null;
  const holderId = (meta.holderOf || {})[ownerId] || ownerId;
  const holderName = (meta.memberName || {})[holderId];
  return (
    <div style={S.who}>
      <span><strong>{name}</strong>{holderId !== ownerId && holderName ? <> · {holderName}'s household</> : null}</span>
      {m.created_by_role === 'admin' && (
        <span style={S.whoPillAdmin} title="Logged by an admin on the investor's behalf">✎ by admin</span>
      )}
      {m.created_by_role === 'executor' && (
        <span style={S.whoPillSelf} title="Logged by the investor">by investor</span>
      )}
    </div>
  );
}

// Movements destined to these kinds are pool contributions/bridges — only
// Kareem's confirm makes them available (guard_pool_confirm enforces it).
// Destinations whose confirm is Kareem's alone (D22 §2 addendum). taekus_corp
// joined this list 2026-08-09 when TaekusPay-straight-into-TK-C became a real
// funding route — without it an investor is shown a Confirm button the
// database will refuse.
const POOL_DEST_KINDS = ['kareem_trust', 'kareem_kraken', 'kareem_gold_bridge', 'taekus_corp'];

// Closest non-failed Kraken deposit within $1-or-0.5% of the draft amount;
// settled deposits win ties over in-flight ones. Stablecoin ≈ USD 1:1, so
// amount proximity is the match signal (plus settlement state for tone).
export function bestDeposit(deposits, amount) {
  const amt = Math.abs(Number(amount) || 0);
  if (!amt || !deposits || !deposits.length) return null;
  const tol = Math.max(1, amt * 0.005);
  let best = null;
  for (const d of deposits) {
    if (d.status === 'failed') continue;
    const diff = Math.abs(Math.abs(Number(d.amount) || 0) - amt);
    if (diff > tol) continue;
    if (!best || diff < best.diff
        || (diff === best.diff && d.status === 'confirmed' && best.d.status !== 'confirmed')) {
      best = { d, diff };
    }
  }
  return best ? best.d : null;
}

// One-line evidence note under a kareem_kraken-destined draft. Purely
// informational — the Confirm button stays the only availability trigger.
export function KrakenEvidence({ movement, deposits, err }) {
  if (err) return <div style={S.evGray}>Kraken evidence unavailable — {err}</div>;
  if (deposits === null) return <div style={S.evGray}>Checking Kraken for a matching deposit…</div>;
  const d = bestDeposit(deposits, movement.amount);
  if (!d) return <div style={S.evGray}>Kraken: no matching deposit in the recent window — verify manually before confirming.</div>;
  const amt = Number(d.amount).toLocaleString('en-US');
  const when = d.at ? new Date(d.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
  if (d.status === 'confirmed') {
    return <div style={S.evOk}>✓ Kraken: +{amt} {d.asset || ''} landed {when} — matches this amount</div>;
  }
  return <div style={S.evWarn}>⏳ Kraken: +{amt} {d.asset || ''} in flight ({when}) — not settled yet</div>;
}

// Inline picker for a draw's suggested step (decision 1: suggestion fills,
// human commits — and the human can always re-point before confirming).
function StepPicker({ candidates, currentStepId, disabled, onSave, onCancel }) {
  const isCurrentCandidate = candidates.some((c) => c.stepId === currentStepId);
  const [v, setV] = useState(isCurrentCandidate ? currentStepId : 'none');
  return (
    <div style={S.edit}>
      <select style={S.select} value={v} autoFocus onChange={(e) => setV(e.target.value)}>
        {candidates.map((c) => (
          <option key={c.stepId} value={c.stepId}>{c.label}</option>
        ))}
        <option value="none">Not a loop step — pool debit only</option>
      </select>
      <button style={{ ...S.btn, ...S.ok }} disabled={disabled} onClick={() => onSave(v === 'none' ? null : v)}>Save</button>
      <button style={S.btn} disabled={disabled} onClick={onCancel}>✕</button>
    </div>
  );
}

export default function PendingDrafts({ member, profile }) {
  const sb = getSupabase();
  const isAdmin = profile?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ acct: {}, kind: {}, loopByStep: {} });
  const [draws, setDraws] = useState({});          // movement_id → pending_draws_for_member row
  const [candByHh, setCandByHh] = useState({});    // household → [{stepId, destKind, label, rate}]
  const [hhOf, setHhOf] = useState({});            // member → household
  const [poolByHh, setPoolByHh] = useState({});    // household → available
  const [fetchState, setFetchState] = useState(null);
  const [hasTkc, setHasTkc] = useState(false);
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(null);    // movement id → amount editor (non-draw)
  const [repointing, setRepointing] = useState(null); // movement id → step picker (draw)
  const [pokeMsg, setPokeMsg] = useState(null);
  const [pokeBusy, setPokeBusy] = useState(false);
  const [deposits, setDeposits] = useState(null); // null = not fetched yet
  const [depErr, setDepErr] = useState(null);
  const [collisions, setCollisions] = useState({}); // movement id → already-logged movement
  const [exempting, setExempting] = useState(null); // movement id → "not pool-related" reason editor

  const load = useCallback(async () => {
    if (!sb) return;
    const { data: mv } = await sb.from('money_movements')
      .select('id, amount, source_account_id, dest_account_id, matched_step_id, notes, status, source, movement_date, movement_time, created_at, source_ref, funded_by_member_id, created_by_role')
      .eq('status', 'pending').order('created_at', { ascending: false });
    let movs = mv || [];

    // Act-as fidelity (T-026, Kareem report 2026-08-08): this list must show
    // the VIEWED household's pending movements, not the session's RLS scope.
    // A real investor session is already scoped by RLS — but an admin ACTING
    // AS an investor queries with admin RLS and sees every household (Kholy's
    // $7,500 draw rendered on Khaled's Home). Whenever a member context
    // exists, scope to that household's accounts; the admin-overview mount
    // (member=null) keeps the everyone view on purpose.
    if (member?.id) {
      const holderId = member.managed_by_member_id || member.id;
      const { data: fam } = await sb.from('members')
        .select('id')
        .or(`id.eq.${holderId},managed_by_member_id.eq.${holderId}`);
      const famIds = (fam || []).map((r) => r.id);
      const { data: hhAccts } = famIds.length
        ? await sb.from('financial_accounts').select('id').in('member_id', famIds)
        : { data: [] };
      const hhAcctIds = new Set((hhAccts || []).map((a) => a.id));
      movs = movs.filter((m) => hhAcctIds.has(m.source_account_id) || hhAcctIds.has(m.dest_account_id));
    }
    setRows(movs);

    // ── Collision disclosure: is this draw already in the ledger by hand? ──
    // Fetch the confirmed rows that could possibly collide (coarse date
    // pre-filter; findCollision does the exact ±12h work). Scoped by the same
    // account ids the draw itself uses, so an admin's wider RLS cannot pull a
    // match out of another household.
    const drawRows = movs.filter((m) => m.source === 'taekus_draw');
    if (!drawRows.length) {
      setCollisions({});
    } else {
      const days = drawRows.map((m) => m.movement_date).filter(Boolean).sort();
      const shift = (d, n) => {
        const t = new Date(d + 'T00:00:00'); t.setDate(t.getDate() + n);
        return t.toISOString().slice(0, 10);
      };
      const drawAcctIds = [...new Set(drawRows.flatMap((m) => [m.source_account_id, m.dest_account_id]).filter(Boolean))];
      const { data: logged } = await sb.from('money_movements')
        .select('id, amount, source_account_id, dest_account_id, movement_date, movement_time, created_at, source, source_ref')
        .eq('status', 'confirmed')
        .in('source_account_id', drawAcctIds)
        .gte('movement_date', shift(days[0], -2))
        .lte('movement_date', shift(days[days.length - 1], 2));
      const found = {};
      drawRows.forEach((m) => {
        const hit = findCollision(m, logged || []);
        if (hit) found[m.id] = hit;
      });
      setCollisions(found);
    }

    // TK-C strip visibility: any mapped card in the caller's RLS scope
    // (own household for investors, everyone for admin).
    sb.from('financial_accounts').select('id', { count: 'exact', head: true })
      .not('taekus_card_uuid', 'is', null).eq('active', true)
      .then(({ count }) => setHasTkc((count || 0) > 0), () => {});
    sb.from('taekus_draw_fetch_state').select('*').eq('id', 1).maybeSingle()
      .then(({ data }) => setFetchState(data || null), () => {});

    if (!movs.length) { setDraws({}); return; }
    const acctIds = [...new Set(movs.flatMap((m) => [m.source_account_id, m.dest_account_id]).filter(Boolean))];
    const stepIds = [...new Set(movs.map((m) => m.matched_step_id).filter(Boolean))];
    // T-044: the accounts fetch also carries member_id, and the members
    // fetch names everyone the viewer's RLS allows (own household for an
    // investor, all for admin) — every row can then say WHOSE money it is.
    const [acctRes, kindRes, refRes, loopRes, memberNameRes] = await Promise.all([
      acctIds.length ? sb.from('financial_accounts').select('id, account_kind, member_id').in('id', acctIds) : { data: [] },
      sb.from('inv_account_kinds').select('name, label'),
      stepIds.length ? sb.from('loop_step_refs').select('step_id, loop_id').in('step_id', stepIds) : { data: [] },
      sb.from('loops').select('id, name'),
      sb.from('members').select('id, name, display_name, managed_by_member_id'),
    ]);
    const acct = Object.fromEntries((acctRes.data || []).map((a) => [a.id, a.account_kind]));
    const acctOwner = Object.fromEntries((acctRes.data || []).map((a) => [a.id, a.member_id]));
    const kind = Object.fromEntries((kindRes.data || []).map((k) => [k.name, k.label]));
    const loopName = Object.fromEntries((loopRes.data || []).map((l) => [l.id, l.name]));
    const loopByStep = {};
    (refRes.data || []).forEach((r) => { loopByStep[r.step_id] = loopName[r.loop_id] || ''; });
    const memberName = {};
    const holderOf = {};
    (memberNameRes.data || []).forEach((mm) => {
      memberName[mm.id] = mm.display_name || mm.name || mm.id;
      holderOf[mm.id] = mm.managed_by_member_id || mm.id;
    });
    setMeta({ acct, acctOwner, kind, loopByStep, memberName, holderOf });

    // ── Draw layer: evidence fields + candidates + pool effect ──
    const hasDraws = movs.some((m) => m.source === 'taekus_draw');
    if (!hasDraws) { setDraws({}); return; }
    const [viewRes, subsRes, activeLoopsRes, refsAllRes, tkcStepsRes, membersRes, poolRes] = await Promise.all([
      sb.from('pending_draws_for_member').select('*'),
      sb.from('loop_subscriptions').select('enrolled_member_id, loop_id').eq('active', true).or('paused.is.null,paused.eq.false'),
      sb.from('loops').select('id, name').eq('active', true),
      sb.from('loop_step_refs').select('loop_id, step_id'),
      sb.from('steps').select('id, dest_kind, value_rate, status').eq('source_kind', 'taekus_corp'),
      sb.from('members').select('id, managed_by_member_id'),
      sb.rpc('v2_pool_position').then((r) => r, () => ({ data: null })),
    ]);
    setDraws(Object.fromEntries((viewRes.data || []).map((d) => [d.movement_id, d])));

    const activeLoopName = Object.fromEntries((activeLoopsRes.data || []).map((l) => [l.id, l.name]));
    const tkcStepById = Object.fromEntries((tkcStepsRes.data || []).map((s) => [s.id, s]));
    const stepsByLoop = {};
    (refsAllRes.data || []).forEach((r) => {
      if (activeLoopName[r.loop_id] == null || !tkcStepById[r.step_id]) return;
      (stepsByLoop[r.loop_id] = stepsByLoop[r.loop_id] || []).push(r.step_id);
    });
    const cands = {};
    (subsRes.data || []).forEach((s) => {
      (stepsByLoop[s.loop_id] || []).forEach((stepId) => {
        const st = tkcStepById[stepId];
        const label = `${activeLoopName[s.loop_id]} · TK-C → ${kind[st.dest_kind] || st.dest_kind}`
          + (st.value_rate ? ` @ ${(Number(st.value_rate) * 100).toFixed(2).replace(/\.?0+$/, '')}%` : '');
        const list = (cands[s.enrolled_member_id] = cands[s.enrolled_member_id] || []);
        if (!list.some((c) => c.stepId === stepId)) {
          list.push({ stepId, destKind: st.dest_kind, label, rate: Number(st.value_rate || 0) });
        }
      });
    });
    setCandByHh(cands);
    setHhOf(Object.fromEntries((membersRes.data || []).map((mm) => [mm.id, mm.managed_by_member_id || mm.id])));
    const pool = {};
    (poolRes.data || []).forEach((p) => { pool[p.household] = Number(p.available || 0); });
    setPoolByHh(pool);
  }, [sb, member?.id, member?.managed_by_member_id]);

  useEffect(() => { load(); }, [load]);

  // Fetch Kraken deposit evidence once per view, only when an admin is
  // looking at at least one kareem_kraken-destined draft. Failures downgrade
  // to a per-row "evidence unavailable" note — never block confirming.
  useEffect(() => {
    if (!isAdmin || !sb || deposits !== null) return undefined;
    const hasKrakenRow = rows.some((m) => meta.acct[m.dest_account_id] === 'kareem_kraken');
    if (!hasKrakenRow) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data: sess } = await sb.auth.getSession();
        const token = sess?.session?.access_token;
        if (!token) throw new Error('no Supabase session');
        const res = await fetch('/.netlify/functions/kraken-read?method=DepositStatus', {
          headers: { Authorization: 'Bearer ' + token },
        });
        const body = await res.json().catch(() => null);
        if (!res.ok) throw new Error((body && body.error) || ('HTTP ' + res.status));
        if (!cancelled) setDeposits(Array.isArray(body && body.deposits) ? body.deposits : []);
      } catch (e) {
        if (!cancelled) { setDeposits([]); setDepErr(e && e.message ? e.message : String(e)); }
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, sb, rows, meta, deposits]);

  const act = async (id, patch) => {
    if (!sb) return;
    setBusy(id);
    const { error } = await sb.from('money_movements').update(patch).eq('id', id);
    if (!error && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('finops:enrollment-changed', { detail: { memberId: member?.id } }));
    }
    setBusy(null);
    setEditing(null);
    setRepointing(null);
    setExempting(null);
    await load();
  };

  // Re-point a pending draw at a different step (or none). The matcher
  // honors a pre-set matched_step_id as a manual override, so the eventual
  // confirm fires on exactly what the human saved here.
  const repoint = async (m, stepId) => {
    if (!sb) return;
    setBusy(m.id);
    try {
      const draw = draws[m.id];
      const memberId = m.funded_by_member_id || draw?.member_id;
      const cand = stepId
        ? Object.values(candByHh).flat().find((c) => c && c.stepId === stepId)
        : null;
      const destKind = stepId ? (cand?.destKind || null) : 'card_spend';
      if (!memberId || !destKind) throw new Error('missing member or destination kind');
      const dstId = await ensureAccount(sb, memberId, destKind, meta.kind[destKind] || destKind);
      const { error } = await sb.from('money_movements')
        .update({ dest_account_id: dstId, matched_step_id: stepId })
        .eq('id', m.id);
      if (error) throw error;
    } catch (e) {
      console.error('repoint failed', e);
    }
    setBusy(null);
    setRepointing(null);
    await load();
  };

  const poke = async () => {
    if (!sb || pokeBusy) return;
    setPokeBusy(true);
    setPokeMsg(null);
    try {
      const { data: sess } = await sb.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token) throw new Error('no session');
      const res = await fetch('/.netlify/functions/taekus-draw-poke', {
        method: 'POST', headers: { Authorization: 'Bearer ' + token },
      });
      const body = await res.json().catch(() => null);
      if (res.status === 429) {
        setPokeMsg(`Just checked — try again in ${body?.retryAfterSeconds || 60}s`);
      } else if (!res.ok) {
        setPokeMsg((body && body.error) || 'Check failed');
      } else {
        setPokeMsg(body?.summary || 'Checked');
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('finops:enrollment-changed', { detail: { memberId: member?.id } }));
        }
      }
    } catch {
      setPokeMsg('Check failed');
    }
    setPokeBusy(false);
    await load();
  };

  const label = (k) => meta.kind[k] || (k || '').replace(/_/g, ' ');
  const flowOf = (m) => `${label(meta.acct[m.source_account_id])} → ${label(meta.acct[m.dest_account_id])}`;

  const checkedAge = fetchState?.last_run_at
    ? Math.max(0, Math.round((Date.now() - new Date(fetchState.last_run_at).getTime()) / 60000))
    : null;
  const checkedLine = checkedAge == null ? 'not checked yet'
    : checkedAge < 1 ? 'checked just now'
    : `checked ${checkedAge} min ago`;

  const drawRows = rows.filter((m) => m.source === 'taekus_draw');
  const otherRows = rows.filter((m) => m.source !== 'taekus_draw');

  // The strip renders for TK-C households even with zero pendings — the
  // "I just ran it, let me confirm now" moment starts with an empty list
  // (decision 5's Check-now half).
  const showStrip = hasTkc && fetchState != null;
  if (!rows.length && !showStrip) return null;

  const renderDrawRow = (m) => {
    const d = draws[m.id] || {};
    const memberId = m.funded_by_member_id || d.member_id;
    const hh = d.household_id || hhOf[memberId] || memberId;
    const candidates = candByHh[hh] || [];
    const cand = candidates.find((c) => c.stepId === m.matched_step_id) || null;
    const isLoopStep = !!cand;
    const avail = poolByHh[hh];
    const when = m.movement_date
      ? new Date(m.movement_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : '';
    return (
      <div key={m.id} style={S.row}>
        <div style={{ minWidth: 220, flex: 1 }}>
          <div style={S.amt}>
            <span style={S.drawTag}>TK-C draw</span>
            {fmtCents(m.amount)}{when ? <span style={{ fontWeight: 500, fontSize: 12, color: 'var(--text-3,#64748b)' }}> · {when}</span> : null}
          </div>
          <div style={S.flow}>
            {d.merchant || 'unknown merchant'}
            {d.card_last4 ? ` · card ·${d.card_last4}` : ''}
            {/* T-044: the card holder's name shows for every viewer, not just
                admin — a multi-card household needs to know WHOSE card drew. */}
            {d.member_name ? ` · ${d.member_name}` : ''}
          </div>
          <div style={S.suggest}>
            {isLoopStep
              ? <>Suggested: <strong>{cand.label}</strong></>
              : <>Not a loop step — <strong>pool debit only</strong></>}
            {' '}
            <button
              type="button"
              style={{ ...S.btn, padding: '2px 8px', fontSize: 11.5 }}
              disabled={busy === m.id}
              onClick={() => setRepointing(repointing === m.id ? null : m.id)}
            >
              change
            </button>
          </div>
          {avail != null && (
            <div style={S.poolFx}>
              Confirming: pool balance {fmtCents(avail)} → {fmtCents(avail - Number(m.amount || 0))}
            </div>
          )}
          {d.degraded && (
            <div style={S.degraded}>⚠ Taekus flagged this row as partially parsed — double-check before confirming.</div>
          )}
          {collisions[m.id] && (() => {
            const hit = collisions[m.id];
            const st = movementStamp(hit);
            return (
              <div style={S.collision}>
                ⚠ <strong>Possible duplicate.</strong> {fmtCents(hit.amount)} for this same route is
                already confirmed in the ledger — {st.kind === 'transacted' ? 'transacted' : 'logged'}{' '}
                <strong>{fmtStamp(st.at)}</strong>.
                <br />
                If that entry is this same charge, tap <strong>Same one — don&apos;t log</strong>. Confirm
                only if this is a second, separate charge.
              </div>
            );
          })()}
        </div>
        {repointing === m.id ? (
          <StepPicker
            candidates={candidates}
            currentStepId={m.matched_step_id}
            disabled={busy === m.id}
            onSave={(stepId) => repoint(m, stepId)}
            onCancel={() => setRepointing(null)}
          />
        ) : (
          <div style={S.actions}>
            <button style={{ ...S.btn, ...S.ok }} disabled={busy === m.id} onClick={() => act(m.id, { status: 'confirmed' })}>
              {collisions[m.id] ? '✓ Different charge — log it' : '✓ Confirm'}
            </button>
            <button style={{ ...S.btn, ...S.no }} disabled={busy === m.id} onClick={() => act(m.id, { status: 'denied' })}>
              {collisions[m.id] ? '🚫 Same one — don’t log' : '🚫 Not mine'}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <section style={S.card}>
      <div style={S.head}>
        🔔 {rows.length ? `${rows.length} movement${rows.length === 1 ? '' : 's'} to review` : 'TK-C card activity'}
      </div>
      {showStrip && (
        <div style={{ ...S.strip, marginBottom: rows.length ? 8 : 0 }}>
          <span>
            {drawRows.length
              ? `${drawRows.length} card draw${drawRows.length === 1 ? '' : 's'} awaiting confirmation`
              : 'No card activity awaiting confirmation'}
            {' · '}{checkedLine}
            {isAdmin && fetchState?.unmapped_open > 0 ? ` · ${fetchState.unmapped_open} on unmapped cards` : ''}
          </span>
          <button type="button" style={S.stripBtn} disabled={pokeBusy} onClick={poke}>
            {pokeBusy ? 'Checking…' : 'Check now'}
          </button>
          {pokeMsg && <span>{pokeMsg}</span>}
        </div>
      )}
      {drawRows.map(renderDrawRow)}
      {otherRows.map((m) => (
        <div key={m.id} style={S.row}>
          <div>
            <div style={S.amt}>{fmt$(m.amount)}</div>
            <div style={S.flow}>
              {flowOf(m)}{meta.loopByStep[m.matched_step_id] ? ` · ${meta.loopByStep[m.matched_step_id]}` : ''}
            </div>
            <WhoLine m={m} meta={meta} />
          </div>
          {editing === m.id ? (
            <EditAmt initial={m.amount} disabled={busy === m.id}
              onCancel={() => setEditing(null)} onSave={(amt) => act(m.id, { amount: amt })} />
          ) : exempting === m.id ? (
            /* Not every TaekusPay into a pool account is a pool contribution.
               "Not mine" would be a lie — the transfer happened — so this
               confirms it as a real movement while marking it out of the pool
               math. The reason is required: pool_exempt_reason's whole job is
               to say WHY a real charge does not count. */
            <ExemptReason disabled={busy === m.id}
              onCancel={() => setExempting(null)}
              onSave={(reason) => act(m.id, { status: 'confirmed', pool_exempt_reason: reason })} />
          ) : (
            <div style={S.actions}>
              {/* Pool contributions: Kareem's confirm is the availability
                  trigger (D22 §2 addendum) — investors see a waiting pill
                  instead of Confirm; the DB guard enforces it regardless. */}
              {(isAdmin || !POOL_DEST_KINDS.includes(meta.acct[m.dest_account_id])) ? (
                <button style={{ ...S.btn, ...S.ok }} disabled={busy === m.id} onClick={() => act(m.id, { status: 'confirmed' })}>✓ Confirm</button>
              ) : (
                <span style={S.waitPill}>⏳ Awaiting Kareem's confirmation</span>
              )}
              <button style={S.btn} disabled={busy === m.id} onClick={() => setEditing(m.id)}>✏️ Edit</button>
              {isAdmin && POOL_DEST_KINDS.includes(meta.acct[m.dest_account_id]) && (
                <button style={S.btn} disabled={busy === m.id} onClick={() => setExempting(m.id)}>
                  ↪ Not pool-related
                </button>
              )}
              <button style={{ ...S.btn, ...S.no }} disabled={busy === m.id} onClick={() => act(m.id, { status: 'denied' })}
                title="Reject it — not this household's, entered twice, or never happened. It stops counting.">🚫 Not mine / duplicate</button>
            </div>
          )}
          {isAdmin && meta.acct[m.dest_account_id] === 'kareem_kraken' && (
            <div style={{ flexBasis: '100%' }}>
              <KrakenEvidence movement={m} deposits={deposits} err={depErr} />
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

function ExemptReason({ onSave, onCancel, disabled }) {
  const [reason, setReason] = useState('');
  const ok = reason.trim().length >= 3;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <input
        type="text"
        autoFocus
        value={reason}
        disabled={disabled}
        placeholder="Why is this not a pool contribution?"
        onChange={(e) => setReason(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && ok) onSave(reason.trim()); if (e.key === 'Escape') onCancel(); }}
        style={{ ...S.select, minWidth: 260, flex: 1 }}
      />
      <button type="button" style={{ ...S.btn, ...S.ok }} disabled={disabled || !ok} onClick={() => onSave(reason.trim())}>
        Record, not pooled
      </button>
      <button type="button" style={S.btn} disabled={disabled} onClick={onCancel}>Cancel</button>
    </div>
  );
}

function EditAmt({ initial, onSave, onCancel, disabled }) {
  const [v, setV] = useState(String(initial ?? ''));
  return (
    <div style={S.edit}>
      <input style={S.input} type="number" inputMode="decimal" value={v} autoFocus
        onChange={(e) => setV(e.target.value)} onWheel={(e) => e.currentTarget.blur()} />
      <button style={{ ...S.btn, ...S.ok }} disabled={disabled}
        onClick={() => { const n = Number(v); if (isFinite(n)) onSave(n); }}>Save</button>
      <button style={S.btn} disabled={disabled} onClick={onCancel}>✕</button>
    </div>
  );
}
